import { ArrowUpOutlined } from '@ant-design/icons'
import { NotesOutlined } from '@mui/icons-material'
import { Spin } from 'antd'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'
import { semantic } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'

import { useCopyToolbar } from 'components/hyper-copy/UseCopyToolbar'
import { PostOverviewSection } from 'components/post-overview/PostOverviewSection'
import {
  CitationBody,
  CitationButton,
  CitationContent,
  CitationFooter,
  CitationHeader,
  CitationHeaderTitle,
  CitationLoading,
  CitationNotFound,
  CitationToolbar,
  ProductChiclet,
  ViewRecordLink,
} from 'features/ask-yogi/features/conversation/components/citation-styles'
import { buildFeedbackUrl } from 'features/ask-yogi/features/conversation/utils/url-builder'
import type { AgentCitationPost } from 'features/ask-yogi/types'
import mixpanel from 'trackers/mixpanel'
import type { PostDetails } from 'types/project'

export interface CitationProps {
  citationId: string
  index: number
  message_uuid?: string
  conversation_uuid?: string
  filterHash?: string
  projectId: string
  citationData?: AgentCitationPost | null
  isLoading: boolean
}

const Citation: React.FC<CitationProps> = ({
  citationId,
  projectId,
  citationData,
  isLoading,
  message_uuid,
  conversation_uuid,
  filterHash,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const postUuid = citationData?.post_uuid
  const verbatimUrl = React.useMemo(() => {
    if (!postUuid) return ''

    return buildFeedbackUrl(projectId, filterHash, postUuid, conversation_uuid)
  }, [projectId, postUuid, filterHash, conversation_uuid])

  // Strip max-height / overflow clips from the cloned DOM so a long verbatim
  // captures its full content, not just the visible box. Citation-only:
  // WYSIWYG matters for HyperDiv tiles, but for a citation the intent is
  // "capture this quote in full." Memoized so useHyperCopy's effect stays
  // stable across parent re-renders.
  const html2canvasOptions = React.useMemo(
    () => ({
      onclone: (_: Document, cloned: HTMLElement) => {
        let node: HTMLElement | null = cloned
        while (node) {
          node.style.maxHeight = 'none'
          node.style.overflow = 'visible'
          node = node.parentElement
        }
      },
    }),
    [],
  )

  const { ref, isHyperCopy, selectForCopy, toolbar } = useCopyToolbar({
    elementName: 'citation-content',
    copyUrl: verbatimUrl,
    message_uuid,
    conversation_uuid,
    html2canvasOptions,
  })

  const popoverContent = isLoading ? (
    <CitationLoading>
      <Spin size="small" />
    </CitationLoading>
  ) : citationData ? (
    <CitationContent>
      <CitationHeader>
        <CitationHeaderTitle>
          Record for
          <ProductChiclet tone="gray" size="lg">
            {citationData.product_hierarchy}
          </ProductChiclet>
        </CitationHeaderTitle>
        <CitationToolbar>{toolbar}</CitationToolbar>
      </CitationHeader>
      <CitationBody
        ref={ref}
        className={isHyperCopy ? 'hyper-copy-active' : 'hyper-copy'}
        onClick={(e) => {
          if (isHyperCopy) {
            selectForCopy()
            e.stopPropagation()
          }
        }}
      >
        <PostOverviewSection
          post={citationData as unknown as PostDetails}
          stacked
          hideImage
        />
      </CitationBody>
      <CitationFooter>
        <ViewRecordLink
          href={verbatimUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            mixpanel.track('askyogi_view_verbatim', {
              proj_uuid: projectId,
              post_id: citationId,
              message_uuid,
              conversation_uuid,
            })
          }}
        >
          View record{' '}
          <ArrowUpOutlined
            style={{ marginLeft: '4px', transform: 'rotate(45deg)' }}
          />
        </ViewRecordLink>
      </CitationFooter>
    </CitationContent>
  ) : (
    <CitationNotFound>Citation data not found</CitationNotFound>
  )

  return (
    <CitationPopover
      content={popoverContent}
      openOnHover
      nativeButton={false}
      initialFocus={false}
      side="right"
      align="end"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
    >
      <CitationButton>
        <NotesOutlined sx={{ fontSize: '14px' }} />
      </CitationButton>
    </CitationPopover>
  )
}

export default Citation

const CitationPopover = styled(Popover)`
  && {
    max-width: 560px;
    padding: 0;
    border-radius: ${radii.lg}px;
    border: 1px solid ${semantic.borderStrong};
    box-shadow: ${shadows.xl};
    overflow: hidden;
  }
`
